import { useContext, useEffect, useMemo, useState, type ContextType } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { ChevronDown, ChevronUp, Grid3X3, Mic, MicOff, Pause, Phone, PhoneOff, Play, Volume2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { VoiceContext } from './GlobalVoiceProvider';
import { flagEmoji } from '@/lib/phoneCountries';

interface BarPhoneNumber {
  phoneNumber: string;
  friendlyName?: string;
  country?: string;
  isoCountry?: string;
  status?: string;
  isActive?: boolean;
  isDefault?: boolean;
}

const STORAGE_KEY = 'dialerBar.collapsed';
const HIDDEN_PREFIXES = ['/onboarding', '/mobile', '/inbox', '/login', '/signup', '/register', '/auth', '/verify-email', '/reset-password', '/forgot-password'];

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function isoFor(n: BarPhoneNumber): string | undefined {
  const explicit = (n.isoCountry || n.country || '').trim();
  if (/^[A-Za-z]{2}$/.test(explicit)) return explicit.toUpperCase();
  try {
    return parsePhoneNumberFromString(n.phoneNumber)?.country;
  } catch {
    return undefined;
  }
}

const CALL_STATUS_LABEL: Record<string, string> = {
  connecting: 'Connecting…',
  ringing: 'Ringing…',
  'on-hold': 'On hold',
};

function formatElapsed(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** The dial bar while a call is live: who, status, and the controls you reach for. */
function InCallBar({ voice }: { voice: NonNullable<ContextType<typeof VoiceContext>> }) {
  const { callState } = voice;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const startedAt = callState.startTime ? new Date(callState.startTime).getTime() : null;
  const elapsed = startedAt ? Math.max(0, Math.floor((now - startedAt) / 1000)) : callState.duration ?? 0;
  const status =
    callState.status === 'connected'
      ? formatElapsed(elapsed)
      : CALL_STATUS_LABEL[callState.status] ?? 'In call';
  const name = callState.contact?.name || callState.number;

  const iconBtn =
    'h-9 w-9 inline-flex items-center justify-center rounded-full border border-border-layer-subtle bg-card text-text-layer-secondary hover:bg-surface-hover';
  const activeBtn = 'bg-primary/10 text-primary border-transparent';

  return (
    <div
      role="region"
      aria-label="Active call"
      data-testid="dialer-bar-incall"
      className="hidden lg:flex fixed bottom-0 left-56 lg:left-60 right-0 z-40 h-14 bg-card border-t border-border-layer-subtle px-4 items-center gap-3"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className="h-9 w-9 shrink-0 inline-flex items-center justify-center rounded-full bg-surface text-text-layer-muted">
          <Phone className="h-4 w-4" />
        </span>
        <div className="min-w-0 leading-tight">
          <div className="truncate text-sm font-semibold text-text-layer-primary" data-testid="dialer-bar-incall-name">{name}</div>
          {callState.contact && <div className="truncate text-xs text-text-layer-muted">{callState.number}</div>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={voice.toggleMute}
          aria-label={voice.isMuted ? 'Unmute' : 'Mute'}
          aria-pressed={voice.isMuted}
          data-testid="dialer-bar-mute"
          className={`${iconBtn} ${voice.isMuted ? activeBtn : ''}`}
        >
          {voice.isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onClick={voice.toggleHold}
          aria-label={voice.isOnHold ? 'Resume' : 'Hold'}
          aria-pressed={voice.isOnHold}
          data-testid="dialer-bar-hold"
          className={`${iconBtn} ${voice.isOnHold ? activeBtn : ''}`}
        >
          {voice.isOnHold ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
        </button>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" aria-label="Keypad" data-testid="dialer-bar-keypad" className={iconBtn}>
              <Grid3X3 className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="center" className="w-56 p-2" data-testid="dialer-bar-keypad-panel">
            <div className="grid grid-cols-3 gap-1.5">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => voice.sendDigits(d)}
                  aria-label={`Send ${d}`}
                  className="h-10 rounded-lg bg-surface text-base font-medium text-text-layer-primary hover:bg-surface-hover"
                >
                  {d}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        <button
          type="button"
          onClick={voice.toggleSpeaker}
          aria-label="Speaker"
          aria-pressed={voice.isSpeakerOn}
          data-testid="dialer-bar-speaker"
          className={`${iconBtn} ${voice.isSpeakerOn ? activeBtn : ''}`}
        >
          <Volume2 className="h-4 w-4" />
        </button>
      </div>
      <div className="flex flex-1 items-center justify-end gap-3">
        <span className="text-sm tabular-nums text-text-layer-secondary" aria-live="polite" data-testid="dialer-bar-incall-status">
          {status}
        </span>
        <button
          type="button"
          onClick={voice.hangUp}
          aria-label="Hang up"
          data-testid="dialer-bar-hangup"
          className="inline-flex h-9 items-center justify-center rounded-full bg-destructive px-5 text-destructive-foreground hover:opacity-90"
        >
          <PhoneOff className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function DialerBar() {
  const voice = useContext(VoiceContext);
  const [location] = useLocation();
  const [value, setValue] = useState('');
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);

  const routeHidden = HIDDEN_PREFIXES.some((p) => location === p || location.startsWith(p + '/') || location.startsWith(p));
  let hasToken = false;
  try {
    hasToken = !!localStorage.getItem('token');
  } catch {
    /* private browsing */
  }

  const { data } = useQuery<BarPhoneNumber[] | { phoneNumbers?: BarPhoneNumber[] }>({
    queryKey: ['/api/organization/phone-numbers'],
    enabled: hasToken && !routeHidden,
  });

  const line = useMemo(() => {
    const list: BarPhoneNumber[] = Array.isArray(data) ? data : data?.phoneNumbers ?? [];
    const usable = list.filter((n) => n && n.phoneNumber && n.isActive !== false);
    return usable.find((n) => n.isDefault) || usable[0];
  }, [data]);

  const callBusy = !!voice && (voice.callState.isActive || ['connecting', 'ringing', 'connected', 'on-hold'].includes(voice.callState.status));
  const inCall = !!voice && callBusy && !routeHidden;
  const visible = !!voice && !!line && !routeHidden && !callBusy && !voice.isDialerVisible;

  useEffect(() => {
    if (!inCall && (!visible || collapsed)) return;
    const prev = document.body.style.paddingBottom;
    document.body.style.paddingBottom = '3.5rem';
    return () => {
      document.body.style.paddingBottom = prev;
    };
  }, [visible, collapsed, inCall]);

  if (inCall && voice) return <InCallBar voice={voice} />;
  if (!visible || !voice || !line) return null;

  const setCollapsedPersist = (next: boolean) => {
    setCollapsed(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  const canCall = /\d/.test(value);

  const submit = () => {
    if (!canCall) return;
    voice.showDialer(value.replace(/[^\d+\s]/g, '').trim());
    setValue('');
  };

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsedPersist(false)}
        aria-label="Show dial bar"
        data-testid="dialer-bar-expand"
        className="hidden lg:flex fixed bottom-0 right-4 z-40 h-6 px-3 items-center rounded-t-md bg-card border border-b-0 border-border-layer-subtle text-muted-foreground hover:text-foreground"
      >
        <ChevronUp className="h-4 w-4" />
      </button>
    );
  }

  const iso = isoFor(line);

  return (
    <div
      role="region"
      aria-label="Quick dialer"
      data-testid="dialer-bar"
      className="hidden lg:flex fixed bottom-0 left-56 lg:left-60 right-0 z-40 h-14 bg-card border-t border-border-layer-subtle px-4 items-center gap-3"
    >
      <div className="flex items-center gap-2 shrink-0 max-w-[16rem] text-sm" data-testid="dialer-bar-line">
        <span aria-hidden="true" className="text-base leading-none">{iso ? flagEmoji(iso) : <Phone className="h-4 w-4" />}</span>
        <span className="truncate font-medium text-foreground">
          {line.friendlyName && line.friendlyName !== line.phoneNumber ? line.friendlyName : 'Main line'}
        </span>
        <span className="text-muted-foreground whitespace-nowrap">{line.phoneNumber}</span>
      </div>
      <input
        type="tel"
        inputMode="tel"
        value={value}
        onChange={(e) => setValue(e.target.value.replace(/[^\d+\s]/g, ''))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="Type a number…"
        aria-label="Phone number to call"
        data-testid="dialer-bar-input"
        className="flex-1 min-w-0 h-9 rounded-full bg-surface px-4 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary"
      />
      <button
        type="button"
        onClick={submit}
        disabled={!canCall}
        aria-label="Call"
        data-testid="dialer-bar-call"
        className="inline-flex items-center gap-2 h-9 rounded-full bg-primary text-primary-foreground px-5 text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Phone className="h-4 w-4" />
        Call
      </button>
      <button
        type="button"
        onClick={() => setCollapsedPersist(true)}
        aria-label="Hide dial bar"
        data-testid="dialer-bar-collapse"
        className="h-8 w-8 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
      >
        <ChevronDown className="h-4 w-4" />
      </button>
    </div>
  );
}
